import { PaginatedTable } from '@avanta/shared/components/PaginatedTable';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useDelayedLoading } from '@avanta/shared/hooks/useDelayedLoading';
import { t } from '@avanta/shared/i18n';
import { formatEUR } from '@avanta/shared/utils/currency';
import { formatDateSimple } from '@avanta/shared/utils/date';
import { type ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { PageHeader } from 'components/PageHeader';
import { TableActions } from './TableActions';
import { type BillingInvoice } from './types';
import { useFetchInvoices } from './useFetchInvoices';

const getColumns = (): ColumnDef<BillingInvoice>[] => [
  {
    id: 'invoiceNumber',
    accessorKey: 'number',
    header: (): string => t('BillingPage.Table.invoiceNumber.label', { ns: LOCIZE_NS.INSURANCE }),
    enableSorting: true,
  },
  {
    id: 'closeDate',
    accessorFn: row => formatDateSimple(row.invoiceDate, 'yyyy-MM-dd'),
    header: (): string => t('BillingPage.Table.closeDate.label', { ns: LOCIZE_NS.INSURANCE }),
    cell: ({ row }): string => formatDateSimple(row.original.invoiceDate, 'yyyy-MM-dd'),
    sortingFn: (firstRow, secondRow): number =>
      firstRow.original.invoiceDate.localeCompare(secondRow.original.invoiceDate),
    enableSorting: true,
  },
  {
    id: 'amount',
    accessorFn: row => (row.totalAmount === null ? '—' : formatEUR(row.totalAmount)),
    header: (): string => t('BillingPage.Table.amount.label', { ns: LOCIZE_NS.INSURANCE }),
    cell: ({ row }): string =>
      row.original.totalAmount === null ? '—' : formatEUR(row.original.totalAmount),
  },
  {
    id: 'actions',
    cell: ({ row }): React.ReactElement => <TableActions invoice={row.original} />,
  },
];

export const Billing = (): React.ReactElement | null => {
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const columns = getColumns();
  const { table, query, selectedDateRange, setDateRange, isMissingCif } = useFetchInvoices(columns);
  const showLoading = useDelayedLoading(query.isFetching);

  if (!table) {
    return null;
  }

  const emptyMessage = t(isMissingCif ? 'BillingPage.missingCif' : 'BillingPage.empty');

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('BillingPage.heading')} description={t('BillingPage.description')} />
      <PaginatedTable<BillingInvoice>
        table={table}
        columns={columns}
        isLoading={showLoading}
        dateRangeFilter={{ accessorKey: 'invoiceDate' }}
        selectedDateRange={selectedDateRange}
        setDateRange={setDateRange}
        searchPlaceholder="BillingPage.search.placeholder"
        searchPlaceholderNs={LOCIZE_NS.INSURANCE}
        emptyTitle={emptyMessage}
        emptyMessage={emptyMessage}
        tableClassName="w-full"
        withSearch
      />
    </div>
  );
};
